/**
 * 추출힌트 자가개선 제안 (M23) API wrapper.
 *
 * 백엔드 엔드포인트 (모두 require_admin):
 *   GET  /api/admin/agent-suggestions/field-errors?min_count&min_rate
 *   POST /api/admin/agent-suggestions/generate
 *   GET  /api/admin/agent-suggestions?status=<pending|approved|rejected>
 *   POST /api/admin/agent-suggestions/{id}/approve
 *   POST /api/admin/agent-suggestions/{id}/reject
 */

import { apiClient } from "./client";

export interface FieldErrorExample {
  before: string;
  after: string;
  // M23_2 — 이미지 기반 힌트 생성용. record_id 가 있으면 원본 페이지를 참조한 것.
  record_id?: string;
  page?: number | null;
}

// extraction_hint: 이미지 근거 위치/구조 힌트 (agent.extraction_rules 반영).
// review_flag    : 위치규칙으로 환원 불가한 상습 오류 (agent.review_rules 반영,
//                   최종 AI 검토에 "재확인" 주의사항으로만 soft 주입, 강제 아님).
export type RuleSuggestionKind = "extraction_hint" | "review_flag";

export interface FieldErrorRow {
  agent_id: string;
  official_name: string | null;
  field: string;
  count: number;
  total: number;
  rate: number;
  examples: FieldErrorExample[];
}

export interface RuleSuggestionEvidence {
  count?: number;
  total?: number;
  rate?: number;
  examples?: FieldErrorExample[];
  current_hint?: string;
  reason?: string;
  rule_key?: string;
  [key: string]: unknown;
}

export type RuleSuggestionStatus = "pending" | "approved" | "rejected";

export interface RuleSuggestionOut {
  id: number;
  agent_id: string;
  official_name: string | null;
  field: string;
  kind: RuleSuggestionKind;
  proposed_hint: string;
  evidence: RuleSuggestionEvidence;
  status: RuleSuggestionStatus;
  created_at: string;
  decided_by: number | null;
  decided_at: string | null;
}

export interface GenerateSuggestionsResult {
  candidates: number;
  created: number;
  updated: number;
  skipped: number;
  extraction_hints: number;
  review_flags: number;
}

export async function fetchFieldErrors(params: {
  minCount?: number;
  minRate?: number;
}): Promise<FieldErrorRow[]> {
  const res = await apiClient.get<FieldErrorRow[]>("/admin/agent-suggestions/field-errors", {
    params: { min_count: params.minCount ?? 1, min_rate: params.minRate ?? 0 },
  });
  return res.data;
}

export async function generateSuggestions(): Promise<GenerateSuggestionsResult> {
  const res = await apiClient.post<GenerateSuggestionsResult>("/admin/agent-suggestions/generate");
  return res.data;
}

export async function listSuggestions(
  status: RuleSuggestionStatus = "pending",
): Promise<RuleSuggestionOut[]> {
  const res = await apiClient.get<RuleSuggestionOut[]>("/admin/agent-suggestions", {
    params: { status },
  });
  return res.data;
}

/** 승인. editedHint 를 주면 관리자가 수정한 최종 문구로 반영된다(공백/미지정 시 원문 유지). */
export async function approveSuggestion(
  id: number,
  editedHint?: string,
): Promise<RuleSuggestionOut> {
  const body = editedHint !== undefined ? { proposed_hint: editedHint } : undefined;
  const res = await apiClient.post<RuleSuggestionOut>(
    `/admin/agent-suggestions/${id}/approve`,
    body,
  );
  return res.data;
}

export async function rejectSuggestion(id: number): Promise<RuleSuggestionOut> {
  const res = await apiClient.post<RuleSuggestionOut>(`/admin/agent-suggestions/${id}/reject`);
  return res.data;
}

export interface RuleSuggestionBacktestItem {
  record_id: string | null;
  expected: string;
  before: string;
  extracted: string | null;
  match: boolean;
  error: string | null;
}

export interface RuleSuggestionBacktest {
  applicable: boolean;
  kind: RuleSuggestionKind;
  field: string;
  rule_key: string | null;
  proposed_hint: string | null;
  message: string | null;
  results: RuleSuggestionBacktestItem[];
  pass_count: number;
  total: number;
}

/**
 * 힌트를 임시로 얹어 예시 문서를 재추출, 같은 오류 재현 여부를 검증.
 * editedHint 를 주면 관리자가 수정 중인 문구로 검증한다(승인 전 미리보기).
 * 예시당 OCR+LLM 재추출이라 수초~수십초 걸릴 수 있음.
 */
export async function backtestSuggestion(
  id: number,
  editedHint?: string,
): Promise<RuleSuggestionBacktest> {
  const body = editedHint !== undefined ? { proposed_hint: editedHint } : undefined;
  const res = await apiClient.post<RuleSuggestionBacktest>(
    `/admin/agent-suggestions/${id}/backtest`,
    body,
  );
  return res.data;
}

export interface SuggestionPagesMeta {
  page_count: number;
}

/** 제안 예시(record_id)가 가리키는 원본 PDF 의 페이지 수. */
export async function getSuggestionRecordPagesMeta(recordId: string): Promise<SuggestionPagesMeta> {
  const res = await apiClient.get<SuggestionPagesMeta>(
    `/admin/agent-suggestions/records/${recordId}/pages/meta`,
  );
  return res.data;
}

/** 제안 예시 레코드의 PDF 페이지 PNG 를 Blob 으로 fetch (admin 전용). */
export async function getSuggestionRecordPageImageBlob(
  recordId: string,
  page: number,
  options?: { signal?: AbortSignal },
): Promise<Blob> {
  const res = await apiClient.get<Blob>(
    `/admin/agent-suggestions/records/${recordId}/pages/${page}/image`,
    { responseType: "blob", signal: options?.signal },
  );
  return res.data;
}
